import { forwardRef } from "react";
import { Mail } from "lucide-react";
import KanbanCard from "./KanbanCard";
import { displayStage } from "@/lib/workspace";
import { phaseForStage } from "@/lib/phases";

const FALLBACK = { tint: "#F1F2F4", accent: "#15233B", border: "#D9DCE1" };

const KanbanColumn = forwardRef(function KanbanColumn(
  {
    template,
    cards,
    users,
    onOpen,
    onDragStart,
    onDrop,
    onDragOver,
    onDragLeave,
    dropState,
    selectable = false,
    selectedIds = new Set(),
    onToggleSelect,
    onHeaderClick,
    isFocused = false,
    idSuffix = "",
    onEmailStage,
  },
  ref
) {
  const p = phaseForStage(template.stage_number)?.pastel || FALLBACK;
  let dropClass = "";
  if (dropState === "valid") dropClass = "ring-2 ring-[#166534] ring-offset-2 ring-offset-[#F1F2F4]";
  else if (dropState === "invalid") dropClass = "ring-2 ring-[#b91c1c]/60 ring-offset-2 ring-offset-[#F1F2F4]";
  if (isFocused) dropClass = "ring-2 ring-[#FB923C] ring-offset-2 ring-offset-[#F1F2F4]";

  return (
    <div
      ref={ref}
      data-testid={`kanban-column-${template.stage_number}${idSuffix}`}
      onDragOver={(e) => onDragOver?.(e, template)}
      onDragLeave={(e) => onDragLeave?.(e, template)}
      onDrop={(e) => onDrop?.(e, template)}
      className={`w-[296px] shrink-0 flex flex-col rounded-lg border bg-white/60 transition-all ${dropClass}`}
      style={{ borderColor: p.border }}
    >
      <div className="flex items-stretch border-b rounded-t-lg" style={{ borderColor: p.border, background: p.tint }}>
      <button
        type="button"
        onClick={() => onHeaderClick?.(template)}
        data-testid={`kanban-column-header-${template.stage_number}${idSuffix}`}
        className="group px-3 py-2.5 flex items-center gap-2 flex-1 min-w-0 text-left rounded-t-lg transition-colors"
        title={`Stage ${displayStage(template.stage_number)} · ${template.day_target}d target`}
      >
        <span className="h-5 w-5 rounded-full grid place-items-center text-[10px] font-extrabold bg-[#15233B] text-white shrink-0">
          {displayStage(template.stage_number)}
        </span>
        <h3 className="text-[12px] font-extrabold tracking-tight text-[#15233B] truncate flex-1 group-hover:opacity-70 transition-opacity">
          {template.name}
        </h3>
        <span
          className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-white/85 shrink-0"
          style={{ color: p.accent }}
        >
          {cards.length}
        </span>
      </button>
      {onEmailStage && (
        <button
          type="button"
          data-testid={`email-all-stage-${template.stage_number}${idSuffix}`}
          onClick={() => onEmailStage(template, cards)}
          title="Email applicants in this stage"
          className="px-2 mr-1.5 my-1.5 rounded-md flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-[0.08em] bg-[#FFCB05] text-[#15233B] hover:bg-[#FBC02D] transition-colors"
        >
          <Mail className="h-3 w-3" /> Email all
        </button>
      )}
      </div>
      <div className="flex-1 flex flex-col gap-2 min-h-[40px] p-2.5">
        {cards.map((card) => (
          <KanbanCard
            key={card.id}
            card={card}
            template={template}
            users={users}
            onOpen={onOpen}
            onDragStart={onDragStart}
            selectable={selectable}
            selected={selectedIds.has(card.id)}
            onToggleSelect={onToggleSelect}
            idSuffix={idSuffix}
          />
        ))}
        {cards.length === 0 && (
          <div className="text-[11px] text-[#15233B]/30 italic py-3 text-center">Drop cards here</div>
        )}
      </div>
    </div>
  );
});

export default KanbanColumn;
